import { FC, useEffect, useState } from 'react';
import styled, { DefaultTheme } from 'styled-components';
import Button from '../Button';
import { CardTheme, cardThemes } from './cardThemes';

const Container = styled.div<{ theme: DefaultTheme; cardTheme: CardTheme }>(({ theme, cardTheme }) => {
  const { color } = theme;
  const { background, image } = cardTheme;

  return `
    position: relative;
    border-radius: 16px;
    padding: 32px 172px 32px 32px;
    color: ${color.grayscale[1000]};
    background: ${background};
    overflow: hidden;

    ${
      image &&
      image.length > 0 &&
      `
        &::after {
          content: '';
          position: absolute;
          right: 0;
          top: 50%;
          transform: translateY(-50%);
          display: block;
          content: url(${image});
          height: 100%;
          width: 100%;
          max-height: 240px;
          max-width: 200px;
        }
      `
    }
  `;
});

const GameImage = styled.img`
  position: absolute;
  right: 0.625rem;
  top: 50%;
  width: 9.375rem;
  transform: translate(0, -50%);
`;

const LiveIcon = styled.div(({ theme }) => {
  const { color, type } = theme;

  return `
    position: relative;
    padding-left: 12px;
    line-height: 1;
    color: ${color.grayscale[1000]};
    font-size: ${type.size.small};
    text-transform: uppercase;
    font-weight: ${type.weight.bold};
    margin-bottom: 2.375rem;

    &:before {
      display: block;
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 6px;
      height: 6px;
      background-color: #70c278;
      border-radius: 50%;
      content: '';
    }
  `;
});

const PlayerCount = styled.div(({ theme }) => {
  return `
    font-size: ${theme.type.size.regular};
    font-weight: ${theme.type.weight.bold};
    margin-left: 30px;
  `;
});

const SinglePlayerSpacer = styled.div(({ theme }) => {
  const { type } = theme;

  return `
    content: ' ';
    margin-bottom: 2.375rem;
    font-size: ${type.size.small};
  `;
});

const Title = styled.h2(({ theme }) => {
  const { color, type } = theme;

  return `
    color: ${color.grayscale[1000]};
    font-weight: ${type.weight.bold};
    font-size: ${type.size.large};
    margin-botom: 0.5rem;
  `;
});

const Description = styled.div(({ theme }) => {
  const { color, type } = theme;

  return `
    color: ${color.grayscale[1000]};
    font-weight: ${type.weight.regular};
    font-size: ${type.size.small};
    margin-bottom: 2.3125rem;
  `;
});

const CtaButton = styled(Button)<{ theme: DefaultTheme; buttonColor: string }>(({ theme, buttonColor }) => {
  const { color } = theme;

  return `
    background ${color.grayscale[1000]};
    color: ${buttonColor};
    border: 1px solid ${color.grayscale[1000]};

    &:hover {
      color: ${color.grayscale[1000]};
      border: 1px solid ${color.grayscale[1000]};
      background: transparent;
    }
  `;
});

const VisuallyHidden = styled.span`
  border: 0;
  clip: rect(0 0 0 0);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  width: 1px;
`;

const BottomRow = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
`;

const Time = styled.div(({ theme }) => {
  const { color, type } = theme;

  return `
    font-size: ${type.size.small};
    color: ${color.grayscale[1000]};
    font-weight: normal;
    line-height: 1;
    margin-left: ${type.size.large};
  `;
});

export const formatTime = (time: number): string => {
  const t = time > 0 ? time : 0;

  const dateTime = new Date(t * 1000);

  let seconds = dateTime.getSeconds().toString();
  let minutes = dateTime.getMinutes().toString();

  if (seconds.length === 1) {
    seconds = '0' + seconds;
  }

  if (minutes.length === 1) {
    minutes = '0' + minutes;
  }

  return `${minutes}:${seconds}`;
};

export interface IGameCard {
  title: string;
  description: string;
  getTimeRef: () => void;
  callback: () => void;
  gameName: string;
  gameImage: string;
  isVisible: boolean;
  multiplayer: boolean;
  hidePlayButton: boolean;
  info: {
    status: string;
    isPersisted: string;
    roomName: string;
    player_count: number;
  };
}

const GameCard: FC<IGameCard> = ({
  title,
  description,
  getTimeRef,
  callback,
  gameName,
  gameImage,
  isVisible,
  multiplayer,
  hidePlayButton,
  info,
}) => {
  const [timeLeft, setTimeLeft] = useState(multiplayer ? getTimeRef() : null);

  const updateTime = () => {
    setTimeLeft(getTimeRef());
  };

  const playerCount = info && info.player_count ? info.player_count : 0;

  let playerCountMessage = '';

  if (playerCount === 1) {
    playerCountMessage = '1 player in the room';
  } else if (playerCount > 0) {
    playerCountMessage = `${playerCount} players in the room`;
  }

  const theme = cardThemes[gameName] || cardThemes.default;

  useEffect(() => {
    if (multiplayer) {
      window.addEventListener('carouselTick', updateTime);

      return () => {
        window.removeEventListener('carouselTick', updateTime);
      };
    }
  });

  const clickHandler = () => {
    if (hidePlayButton) {
      return;
    }

    callback();
  };

  const imageSource = new URL(`../../../static/assets/game_icons/${gameImage}`, import.meta.url).href;

  return (
    <Container cardTheme={theme} aria-hidden={!isVisible} onClick={clickHandler}>
      {multiplayer ? <LiveIcon>LIVE</LiveIcon> : <SinglePlayerSpacer>&nbsp;</SinglePlayerSpacer>}

      <Title>{title}</Title>

      <Description>{description}</Description>
      {!theme.image && <GameImage src={imageSource} />}
      <BottomRow>
        {!hidePlayButton && (
          <CtaButton compressed rounded type="button" buttonColor={theme.buttonColor} tabIndex={isVisible ? 0 : -1}>
            Join game
            <VisuallyHidden>{title}</VisuallyHidden>
          </CtaButton>
        )}

        {multiplayer && typeof timeLeft === 'number' && <Time aria-hidden>{formatTime(timeLeft)}</Time>}
        {multiplayer && playerCount > 0 && <PlayerCount>{playerCountMessage}</PlayerCount>}
      </BottomRow>
    </Container>
  );
};

export default GameCard;
